<script setup>
import moment from 'moment'
import { titleCase } from '@/@core/utils/formatters'
import { can } from '@/@layouts/plugins/casl'
import RiskPromoteDialog from '@/views/quality/risk/RiskPromoteDialog.vue'

definePage({
  meta: {
    name: 'incident-audit-view',
    subject: 'quality.menu',
    action: 'audit_collection',
  },
})

const route = useRoute()
const router = useRouter()

const collectionId = computed(() => route.params.id)

// Dynamic back route based on where user came from
const backRouteName = computed(() => route.meta.navActiveLink ?? 'app-quality-audit-reporting')

// Fetch audit collection
const auditCollection = ref(null)
const loading = ref(true)

await useApi(createUrl(`/incident/audit-collections/${collectionId.value}`, {
  query: {
    with: ['audit', 'assessmentType', 'location', 'unit', 'submittedByUser', 'questionAnswers.incidentQuestion', 'questionAnswers.incidentQuestionOption', 'incidentCapas', 'findings.question'],
  },
}), {
  afterFetch: ({ data }) => {
    auditCollection.value = data
    loading.value = false
  },
  onFetchError: () => {
    loading.value = false
    router.replace({
      name: 'invalid-url',
      params: { code: 404 },
    })
  },
})

// Status color resolver
const getStatusColor = status => {
  const colors = {
    SUBMITTED: 'success',
    DRAFT: 'warning',
    OVERDUE: 'error',
    PENDING: 'info',
  }

  return colors[status?.toUpperCase()] ?? 'secondary'
}

// Audit details for AppCardList
const auditDetails = computed(() => {
  const data = auditCollection.value
  if (!data) return []

  const items = [
    { label: 'Audit', value: data.audit?.title ?? '', icon: 'tabler-clipboard-check' },
    { label: 'Assessment Type', value: data.assessment_type?.name ?? '', icon: 'tabler-category' },
    { label: 'Location', value: data.location?.name ?? '', icon: 'tabler-map-pin' },
    { label: 'Unit', value: data.unit?.name ?? '', icon: 'tabler-building' },
    { label: 'Status', value: titleCase(data.status ?? ''), icon: 'tabler-activity', isChip: true },
    { label: 'Submitted By', value: data.submitted_by_user?.name ?? '', icon: 'tabler-user' },
    { label: 'Submitted At', value: data.submitted_at ? moment(data.submitted_at).format('hh:mm A, Do MMM YYYY') : '', icon: 'tabler-calendar-event' },
  ]

  return items.filter(item => item.value)
})

// Customer details for AppCardList (shown only if customer data exists)
const customerDetails = computed(() => {
  const data = auditCollection.value
  if (!data) return []

  const items = [
    { label: 'Name', value: data.customer?.name ?? data.customer_name ?? '', icon: 'tabler-user-heart' },
    { label: 'UHID', value: data.customer?.uhid ?? data.customer_uhid ?? '', icon: 'tabler-id' },
  ]

  if (data.episode) {
    items.push(
      { label: 'Episode Code', value: data.episode.code ?? '', icon: 'tabler-hash' },
      { label: 'Doctor', value: data.episode.doctor_name ?? '', icon: 'tabler-stethoscope' },
    )
  }

  return items.filter(item => item.value)
})

const hasCustomerData = computed(() => customerDetails.value.length > 0)

// Fetch question groups for the assessment type
const assessmentTypeId = computed(() => auditCollection.value?.assessment_type_id)

const { data: questionGroupsData } = useApi(createUrl('/incident/question-groups', {
  query: {
    search: {
      assessment_type_id: assessmentTypeId,
    },
    with: ['questions.questionOptions'],
  },
}))

const questionGroups = computed(() => {
  const raw = questionGroupsData.value
  if (!raw) return []

  const items = Array.isArray(raw) ? raw : (raw?.data ?? [])

  return items
    .sort((a, b) => a.order - b.order)
    .map(group => ({
      ...group,
      questions: (group.questions ?? []).sort((a, b) => a.order - b.order),
    }))
})

// Build answer lookup from question_answers
const answerMap = computed(() => {
  const map = {}
  const answers = auditCollection.value?.question_answers ?? []

  answers.forEach(qa => {
    map[qa.incident_question_id] = qa
  })

  return map
})

// Map a single question_answer to an AppQAList item
const mapQaToItem = qa => {
  const question = qa.incident_questions
  let answer = ''
  let attachment = null

  if (question?.question_type === 'ai_voice' && qa.attachment)
    attachment = qa.attachment

  if (qa.question_option)
    answer = qa.question_option.label ?? qa.question_option.value ?? ''
  else if (qa.response !== null && qa.response !== undefined)
    answer = typeof qa.response === 'object'
      ? (qa.response.label ?? qa.response.value ?? JSON.stringify(qa.response))
      : String(qa.response)

  if (question?.question_type === 'smile' && qa.question_option)
    answer = qa.question_option.label ?? ''

  return {
    question: question?.label ?? `Question #${qa.incident_question_id}`,
    answer,
    question_type: question?.question_type ?? 'text',
    answer_reason: qa.reason_response || null,
    attachment,
    attachments: qa.attachments ?? [],
  }
}

// Group Q&A items by question group; nest answered children under their parent
const groupedQaItems = computed(() => {
  if (!questionGroups.value.length) return []

  return questionGroups.value
    .map(group => {
      const questions = group.questions ?? []
      const roots = questions.filter(q => !q.parent_question_id)

      const hasContent = item =>
        item.answer || item.attachment || item.attachments?.length

      const items = roots
        .filter(root => answerMap.value[root.id])
        .map(root => {
          const item = mapQaToItem(answerMap.value[root.id])

          item.sub_items = questions
            .filter(q => q.parent_question_id === root.id && answerMap.value[q.id])
            .map(child => mapQaToItem(answerMap.value[child.id]))
            .filter(sub => hasContent(sub))

          return item
        })
        .filter(item => hasContent(item) || item.sub_items.length)

      return {
        id: group.id,
        title: group.title || group.name,
        items,
      }
    })
    .filter(group => group.items.length > 0)
})

const hasResponses = computed(() => groupedQaItems.value.some(g => g.items.length > 0))

// ─── Bundle Compliance ───

const isBundleCompliance = computed(() => auditCollection.value?.audit?.type === 'BUNDLE_COMPLIANCE')

function normalizeOptionValue(val) {
  if (val === null || val === undefined || val === '') return NaN
  if (typeof val === 'string') {
    const lower = val.toLowerCase()
    if (lower === 'yes') return 1
    if (lower === 'no') return 0
  }

  return Number(val)
}

function getAllQuestions() {
  const result = []
  for (const group of questionGroups.value) {
    for (const question of group.questions || []) {
      result.push(question)
    }
  }

  return result
}

const complianceQuestions = computed(() => {
  return getAllQuestions().filter(q => {
    if (!q.is_compliance || q.is_compliance == 0) return false

    const options = q.question_options ?? []
    if (options.length === 0) return false

    return options.every(opt => !isNaN(normalizeOptionValue(opt.value)))
  })
})

const hasComplianceQuestions = computed(() => isBundleCompliance.value && complianceQuestions.value.length > 0)

const complianceScore = computed(() => {
  let score = 0

  complianceQuestions.value.forEach(q => {
    const qa = answerMap.value[q.id]
    if (qa?.question_option) {
      const val = normalizeOptionValue(qa.question_option.value)
      if (!isNaN(val)) score += val
    }
  })

  return score
})

const totalScore = computed(() => {
  let sum = 0

  complianceQuestions.value.forEach(q => {
    const options = q.question_options ?? []

    sum += Math.max(...options.map(opt => normalizeOptionValue(opt.value)))
  })

  return sum
})

const compliancePercentage = computed(() => {
  if (totalScore.value === 0) return 0

  return Math.round((complianceScore.value / totalScore.value) * 100)
})

const complianceColor = computed(() => {
  if (compliancePercentage.value >= 80) return 'success'
  if (compliancePercentage.value >= 50) return 'warning'

  return 'error'
})

// ─── CAPA ───

const capas = computed(() => auditCollection.value?.incident_capas ?? [])
const hasCapas = computed(() => capas.value.length > 0)

const capaStatusColor = status => {
  const colors = {
    PENDING: 'warning',
    COMPLETED: 'success',
    OVERDUE: 'error',
  }

  return colors[status?.toUpperCase()] ?? 'info'
}

// ─── Findings & Promote to Risk ───

// AuditScoringService only ever persists a finding for a non-compliant item
// (finding_type FAILED or UNANSWERED) — a fully-scored, passing question never
// gets a row. So every finding here already represents a genuine compliance
// gap, and every one is a legitimate promotion candidate; there is no
// "passing finding" case to filter out before offering the action.
const findings = computed(() => auditCollection.value?.findings ?? [])
const hasFindings = computed(() => findings.value.length > 0)

const findingTypeColor = type => (type === 'UNANSWERED' ? 'warning' : 'error')
const findingTypeIcon = type => (type === 'UNANSWERED' ? 'tabler-help-circle' : 'tabler-x')
const criticalityColor = criticality => (criticality === 'CRITICAL' ? 'error' : 'secondary')

const { isOpen: isPromoteDialogOpen, source: promoteSource, promptPromote } = useRiskPromotion()

const canPromote = computed(() => can('promote', 'quality.risk'))

const openPromoteDialog = finding => {
  const questionLabel = finding.question?.label ?? 'this checklist item'

  promptPromote('AUDIT_FINDING', finding.id, finding.unit_id, {
    title: `Audit Finding: ${questionLabel}`,
    description: `${titleCase(finding.finding_type ?? '')} finding on "${questionLabel}" during "${auditCollection.value?.audit?.title ?? 'this audit'}" (${titleCase(finding.criticality ?? '')} criticality, scored ${finding.selected_value ?? 0}/${finding.max_value ?? 0}).`,
  })
}

const onPromoted = risk => {
  router.push({ name: 'app-quality-risk-id', params: { id: risk.id } })
}

// Export audit collection
const exportAudit = () => {
  const url = `${import.meta.env.VITE_API_BASE_URL}/incident/audit-collections/${collectionId.value}/export`

  window.open(url, '_blank')
}
</script>

<template>
  <section v-if="!loading && auditCollection">
    <!-- Page Header -->
    <div class="d-flex align-center gap-4 mb-6">
      <IconBtn
        variant="tonal"
        class="rounded"
        size="large"
        @click="router.push({ name: backRouteName })"
      >
        <VIcon
          size="24"
          icon="tabler-arrow-left"
        />
      </IconBtn>
      <div class="flex-1-1">
        <div class="d-flex align-center gap-3 flex-wrap">
          <div class="text-h5">
            {{ auditCollection.audit?.title ?? 'Audit Collection' }}
          </div>
          <VChip
            size="small"
            :color="getStatusColor(auditCollection.status)"
            variant="tonal"
            class="text-capitalize font-weight-medium"
          >
            {{ titleCase(auditCollection.status ?? '') }}
          </VChip>
        </div>
        <span class="text-body-2 text-medium-emphasis">
          {{ auditCollection.submitted_at ? `Submitted on ${moment(auditCollection.submitted_at).format('Do MMM YYYY, hh:mm A')}` : 'Audit collection details' }}
        </span>
      </div>

      <VBtn
        variant="tonal"
        color="primary"
        @click="exportAudit"
      >
        <VIcon
          icon="tabler-download"
          start
        />
        Export
      </VBtn>
    </div>

    <VRow>
      <!-- Left Column: Q&A -->
      <VCol
        cols="12"
        md="7"
        order="2"
        order-md="1"
      >
        <!-- Compliance Score Cards -->
        <VRow
          v-if="hasComplianceQuestions"
          class="mb-6"
        >
          <VCol
            cols="12"
            md="4"
          >
            <VCard
              variant="flat"
              class="border text-center pa-4"
            >
              <div class="text-body-2 text-medium-emphasis mb-1">
                Total Score
              </div>
              <div class="text-h5 font-weight-medium">
                {{ totalScore }}
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ complianceQuestions.length }} Questions
              </div>
            </VCard>
          </VCol>
          <VCol
            cols="12"
            md="4"
          >
            <VCard
              variant="flat"
              class="border text-center pa-4"
            >
              <div class="text-body-2 text-medium-emphasis mb-1">
                Compliance Score
              </div>
              <div class="text-h5 font-weight-medium text-primary">
                {{ complianceScore }}
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ complianceScore }} / {{ totalScore }}
              </div>
            </VCard>
          </VCol>
          <VCol
            cols="12"
            md="4"
          >
            <VCard
              variant="flat"
              class="border text-center pa-4"
            >
              <div class="text-body-2 text-medium-emphasis mb-1">
                Compliance Percentage
              </div>
              <div
                class="text-h5 font-weight-medium"
                :class="`text-${complianceColor}`"
              >
                {{ compliancePercentage }}%
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ complianceScore }} / {{ totalScore }} × 100
              </div>
            </VCard>
          </VCol>
        </VRow>

        <VCard
          v-if="hasResponses"
          variant="flat"
          class="border"
        >
          <VCardItem>
            <template #title>
              <div class="d-flex align-center gap-2">
                <VAvatar
                  color="primary"
                  variant="tonal"
                  size="32"
                >
                  <VIcon
                    icon="tabler-box"
                    size="18"
                  />
                </VAvatar>
                <div class="text-h6 font-weight-medium text-primary">
                  Audit Responses
                </div>
              </div>
            </template>
          </VCardItem>
          <VDivider />

          <template
            v-for="(group, index) in groupedQaItems"
            :key="group.id"
          >
            <VCardText>
              <div class="group-header py-3 px-1 d-flex align-center gap-2">
                <div class="group-indicator" />
                <span class="text-subtitle-1 font-weight-medium">{{ group.title }}</span>
              </div>

              <AppQAList
                :items="group.items"
                header-icon="tabler-clipboard-check"
                header-color="primary"
              />
            </VCardText>

            <VDivider v-if="index < groupedQaItems.length - 1" />
          </template>
        </VCard>

        <div
          v-if="!hasResponses"
          class="text-center text-medium-emphasis pa-8 border rounded"
        >
          No responses recorded for this audit collection.
        </div>
      </VCol>

      <!-- Right Column: Details (sticky) -->
      <VCol
        cols="12"
        md="5"
        order="1"
        order-md="2"
      >
        <div class="sidebar-sticky">
          <VCard
            variant="flat"
            class="border"
          >
            <VCardText>
              <AppCardList
                :items="auditDetails"
                title="Audit Details"
                header-icon="tabler-file-text"
                header-color="primary"
                :chip-color-resolver="getStatusColor"
                :max-value-width="280"
              />

              <AppCardList
                v-if="hasCustomerData"
                :items="customerDetails"
                :title="$t('Customer') + ' Details'"
                header-icon="tabler-user-heart"
                header-color="info"
                :max-value-width="280"
                class="mt-6"
              />
            </VCardText>
          </VCard>

          <!-- CAPA Section -->
          <VCard
            v-if="hasCapas"
            variant="flat"
            class="border mt-6"
          >
            <VCardItem>
              <template #title>
                <div class="d-flex align-center gap-2">
                  <VAvatar
                    color="error"
                    variant="tonal"
                    size="32"
                  >
                    <VIcon
                      icon="tabler-alert-triangle"
                      size="18"
                    />
                  </VAvatar>
                  <span class="text-subtitle-1 font-weight-medium text-error">
                    CAPA Raised
                  </span>
                </div>
              </template>
            </VCardItem>
            <VDivider />
            <VCardText>
              <div
                v-for="(capaItem, idx) in capas"
                :key="capaItem.id"
              >
                <div
                  v-if="idx > 0"
                  class="my-4"
                >
                  <VDivider />
                </div>

                <div class="d-flex align-center justify-space-between mb-3">
                  <span class="text-body-2 font-weight-medium">
                    CAPA #{{ idx + 1 }}
                  </span>
                  <VChip
                    size="x-small"
                    :color="capaStatusColor(capaItem.status)"
                    variant="tonal"
                  >
                    {{ titleCase(capaItem.status ?? 'Pending') }}
                  </VChip>
                </div>

                <div class="d-flex flex-column gap-2">
                  <div class="d-flex text-body-2">
                    <span class="capa-label text-medium-emphasis">Corrective Action:</span>
                    <span class="font-weight-medium">{{ capaItem.corrective_action ?? 'N/A' }}</span>
                  </div>
                  <div
                    v-if="capaItem.corrective_action_due_date"
                    class="d-flex text-body-2"
                  >
                    <span class="capa-label text-medium-emphasis">C/A Due Date:</span>
                    <span>{{ moment(capaItem.corrective_action_due_date).format('Do MMM YYYY') }}</span>
                  </div>
                  <div
                    v-if="capaItem.preventive_action"
                    class="d-flex text-body-2"
                  >
                    <span class="capa-label text-medium-emphasis">Preventive Action:</span>
                    <span class="font-weight-medium">{{ capaItem.preventive_action }}</span>
                  </div>
                  <div
                    v-if="capaItem.preventive_action_due_date"
                    class="d-flex text-body-2"
                  >
                    <span class="capa-label text-medium-emphasis">P/A Due Date:</span>
                    <span>{{ moment(capaItem.preventive_action_due_date).format('Do MMM YYYY') }}</span>
                  </div>
                </div>
              </div>
            </VCardText>
          </VCard>

          <!-- Findings Section -->
          <VCard
            v-if="hasFindings"
            variant="flat"
            class="border mt-6"
          >
            <VCardItem>
              <template #title>
                <div class="d-flex align-center gap-2">
                  <VAvatar
                    color="error"
                    variant="tonal"
                    size="32"
                  >
                    <VIcon
                      icon="tabler-alert-triangle"
                      size="18"
                    />
                  </VAvatar>
                  <span class="text-subtitle-1 font-weight-medium text-error">
                    Audit Findings
                  </span>
                </div>
              </template>
            </VCardItem>
            <VDivider />
            <VCardText>
              <div
                v-for="(finding, idx) in findings"
                :key="finding.id"
              >
                <div
                  v-if="idx > 0"
                  class="my-4"
                >
                  <VDivider />
                </div>

                <div class="d-flex align-center justify-space-between gap-2 mb-2">
                  <span class="text-body-2 font-weight-medium">
                    {{ finding.question?.label ?? `Finding #${finding.id}` }}
                  </span>
                  <div class="d-flex gap-2 flex-shrink-0">
                    <VChip
                      size="x-small"
                      :color="findingTypeColor(finding.finding_type)"
                      variant="tonal"
                    >
                      <template #prepend>
                        <VIcon
                          :icon="findingTypeIcon(finding.finding_type)"
                          size="16"
                          class="me-1"
                        />
                      </template>
                      <span>{{ titleCase(finding.finding_type ?? '') }}</span>
                    </VChip>
                    <VChip
                      size="x-small"
                      :color="criticalityColor(finding.criticality)"
                      variant="tonal"
                    >
                      {{ titleCase(finding.criticality ?? '') }}
                    </VChip>
                  </div>
                </div>

                <div class="d-flex align-center justify-space-between gap-2">
                  <span class="text-caption text-medium-emphasis">
                    Scored {{ finding.selected_value ?? 0 }} / {{ finding.max_value ?? 0 }}
                  </span>
                  <VBtn
                    v-if="canPromote"
                    size="small"
                    variant="tonal"
                    color="primary"
                    prepend-icon="tabler-shield-plus"
                    @click="openPromoteDialog(finding)"
                  >
                    Promote to Risk
                  </VBtn>
                </div>
              </div>
            </VCardText>
          </VCard>

          <!-- Resume Draft Card -->
          <VCard
            v-if="auditCollection.status === 'DRAFT'"
            variant="flat"
            class="border mt-6"
          >
            <VCardText>
              <p class="text-body-2 mb-3">
                This audit collection is still in draft. You can resume filling it.
              </p>
              <VBtn
                color="primary"
                block
                @click="router.push({ name: 'app-quality-audit-collect', params: { id: auditCollection.id } })"
              >
                <VIcon
                  icon="tabler-player-play"
                  start
                />
                Resume Audit
              </VBtn>
            </VCardText>
          </VCard>
        </div>
      </VCol>
    </VRow>

    <RiskPromoteDialog
      v-model:is-dialog-open="isPromoteDialogOpen"
      :source-type="promoteSource.type"
      :source-id="promoteSource.id"
      :source-unit-id="promoteSource.unitId"
      :prefill="promoteSource.prefill"
      @promoted="onPromoted"
    />
  </section>

  <!-- Loading state -->
  <section v-else-if="loading">
    <div class="d-flex align-center gap-4 mb-6">
      <VSkeletonLoader
        type="avatar"
        width="48"
      />
      <div>
        <VSkeletonLoader
          type="text"
          width="250"
        />
        <VSkeletonLoader
          type="text"
          width="180"
        />
      </div>
    </div>
    <VRow>
      <VCol
        cols="12"
        md="8"
      >
        <VSkeletonLoader type="card" />
      </VCol>
      <VCol
        cols="12"
        md="4"
      >
        <VSkeletonLoader type="card" />
      </VCol>
    </VRow>
  </section>
</template>

<style scoped>
.sidebar-sticky {
  position: sticky;
  inset-block-start: 80px;
}

.group-header {
  /* background: rgba(var(--v-theme-primary), 0.08); */
}

.group-indicator {
  inline-size: 4px;
  block-size: 24px;
  background: rgb(var(--v-theme-primary));
  border-radius: 2px;
}

.capa-label {
  inline-size: 150px;
  flex-shrink: 0;
}
</style>
